import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Users, Eye, Edit, Trash2, Plus, Save, RefreshCw,
  LayoutDashboard, Building2, Package, TrendingUp, DollarSign,
  FolderKanban, Calendar, Share2, Sparkles, Settings, Lock,
  UserCog, Check, X, ChevronRight, AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for sidebar items
const ICON_MAP = {
  dashboard: LayoutDashboard,
  clients: Building2,
  services: Package,
  sales: TrendingUp,
  finance: DollarSign,
  projects: FolderKanban,
  calendar: Calendar,
  social: Share2,
  ai: Sparkles,
  team: Users,
  permissions: Shield,
  settings: Settings,
};

// Role badge colors
const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  sales: 'bg-green-100 text-green-800 border-green-200',
  support: 'bg-orange-100 text-orange-800 border-orange-200',
  viewer: 'bg-gray-100 text-gray-800 border-gray-200',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  sales: 'Sales',
  support: 'Support',
  viewer: 'Viewer',
};

// Sidebar Item Configuration Card
const SidebarItemCard = ({ item, roles, onUpdate }) => {
  const Icon = ICON_MAP[item.item_key] || Settings;
  const [localItem, setLocalItem] = useState(item);

  const handleToggleRole = (role) => {
    const newRoles = localItem.visible_roles.includes(role)
      ? localItem.visible_roles.filter(r => r !== role)
      : [...localItem.visible_roles, role];
    const updated = { ...localItem, visible_roles: newRoles };
    setLocalItem(updated);
    onUpdate(updated);
  };

  const handleTogglePermission = (perm) => {
    const updated = { 
      ...localItem, 
      permissions: { 
        ...localItem.permissions, 
        [perm]: !localItem.permissions[perm] 
      } 
    };
    setLocalItem(updated);
    onUpdate(updated);
  };

  const handleToggleEnabled = () => {
    const updated = { ...localItem, enabled: !localItem.enabled };
    setLocalItem(updated);
    onUpdate(updated);
  };

  return (
    <Card className={`transition-all ${!localItem.enabled ? 'opacity-60 bg-gray-50' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${localItem.enabled ? 'bg-black text-[#D4E100]' : 'bg-gray-200 text-gray-500'}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-semibold text-sm">{item.name || item.item_key}</h4>
              <p className="text-xs text-muted-foreground">Key: {item.item_key}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Enabled</Label>
            <Switch 
              checked={localItem.enabled} 
              onCheckedChange={handleToggleEnabled}
              data-testid={`toggle-${item.item_key}`}
            />
          </div>
        </div>

        {localItem.enabled && (
          <>
            {/* Visible Roles */}
            <div className="mt-4">
              <Label className="text-xs font-medium text-muted-foreground">Visible to Roles:</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {roles.map(role => (
                  <Badge 
                    key={role.role}
                    variant="outline"
                    className={`cursor-pointer transition-all ${
                      localItem.visible_roles.includes(role.role) 
                        ? ROLE_COLORS[role.role] 
                        : 'bg-white text-gray-400 border-dashed'
                    }`}
                    onClick={() => handleToggleRole(role.role)}
                  >
                    {localItem.visible_roles.includes(role.role) ? (
                      <Check className="h-3 w-3 mr-1" />
                    ) : (
                      <X className="h-3 w-3 mr-1" />
                    )}
                    {ROLE_LABELS[role.role] || role.role}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Permissions */}
            <div className="mt-4">
              <Label className="text-xs font-medium text-muted-foreground">Permissions:</Label>
              <div className="flex gap-4 mt-2">
                {['view', 'create', 'edit', 'delete'].map(perm => (
                  <div key={perm} className="flex items-center gap-1">
                    <Checkbox 
                      id={`${item.item_key}-${perm}`}
                      checked={localItem.permissions[perm] || false}
                      onCheckedChange={() => handleTogglePermission(perm)}
                    />
                    <Label htmlFor={`${item.item_key}-${perm}`} className="text-xs capitalize cursor-pointer">
                      {perm}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Role Management Card
const RoleCard = ({ role, onEdit }) => {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              role.role === 'super_admin' ? 'bg-purple-600' : 
              role.role === 'admin' ? 'bg-blue-600' : 
              role.role === 'sales' ? 'bg-green-600' : 
              role.role === 'support' ? 'bg-orange-600' : 'bg-gray-600'
            } text-white`}>
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-semibold">{ROLE_LABELS[role.role] || role.role}</h4>
              <p className="text-xs text-muted-foreground">{role.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">Level {role.level || 0}</Badge>
            {role.role !== 'super_admin' && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(role)}>
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// User Override Card
const UserOverrideCard = ({ user, override, onEdit, onRemove }) => {
  return (
    <Card data-testid={`user-override-card-${user.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-black text-[#D4E100] flex items-center justify-center font-bold">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h4 className="font-semibold">{user.name}</h4>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={ROLE_COLORS[user.role]}>{ROLE_LABELS[user.role] || user.role}</Badge>
            {override?.override_role && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200" data-testid={`override-active-${user.id}`}>
                <Lock className="h-3 w-3 mr-1" /> Override Active
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => onEdit(user)} data-testid={`edit-override-${user.id}`}>
              <UserCog className="h-4 w-4" />
            </Button>
            {override?.override_role && (
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onRemove(user.id)} data-testid={`remove-override-${user.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Permissions Page
export const PermissionsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sidebarConfig, setSidebarConfig] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [userOverrides, setUserOverrides] = useState({});
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOverride, setSelectedOverride] = useState({
    override_role: false,
    sidebar_items: [],
    permissions: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, rolesRes, usersRes] = await Promise.all([
        axios.get(`${API}/permissions/sidebar-config`),
        axios.get(`${API}/permissions/roles`),
        axios.get(`${API}/users`)
      ]);
      
      setSidebarConfig(configRes.data.items || []);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
      
      // Load overrides for each user
      const overrides = {};
      for (const u of usersRes.data) {
        try {
          const overrideRes = await axios.get(`${API}/permissions/user/${u.id}`);
          if (overrideRes.data.override_role) {
            overrides[u.id] = overrideRes.data;
          }
        } catch (e) {
          // No override for this user
        }
      }
      setUserOverrides(overrides);
    } catch (error) {
      console.error('Failed to load permissions data:', error);
      toast.error('Failed to load permissions data');
    } finally {
      setLoading(false);
    }
  };

  const handleSidebarItemUpdate = (updatedItem) => {
    setSidebarConfig(prev => 
      prev.map(item => item.item_key === updatedItem.item_key ? updatedItem : item)
    );
  };

  const saveSidebarConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/permissions/sidebar-config`, { items: sidebarConfig });
      toast.success('Sidebar configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save sidebar configuration');
    } finally {
      setSaving(false);
    }
  };

  const openUserOverrideDialog = async (selectedUserData) => {
    setSelectedUser(selectedUserData);
    try {
      const res = await axios.get(`${API}/permissions/user/${selectedUserData.id}`);
      setSelectedOverride({
        override_role: res.data.override_role || false,
        sidebar_items: res.data.sidebar_items || [],
        permissions: res.data.permissions || {}
      });
    } catch (e) {
      setSelectedOverride({
        override_role: false,
        sidebar_items: sidebarConfig.map(i => i.item_key),
        permissions: {}
      });
    }
    setEditUserDialog(true);
  };

  const saveUserOverride = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/permissions/user/${selectedUser.id}`, {
        user_id: selectedUser.id,
        ...selectedOverride
      });
      toast.success(`Override saved for ${selectedUser.name}`);
      setEditUserDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save user override');
    } finally {
      setSaving(false);
    }
  };

  const removeUserOverride = async (userId) => {
    try {
      await axios.delete(`${API}/permissions/user/${userId}`);
      toast.success('User override removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove override');
    }
  };

  const toggleOverrideSidebarItem = (itemKey) => {
    setSelectedOverride(prev => ({
      ...prev,
      sidebar_items: prev.sidebar_items.includes(itemKey)
        ? prev.sidebar_items.filter(k => k !== itemKey)
        : [...prev.sidebar_items, itemKey]
    }));
  };

  // Check if current user is super_admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only Super Admin can access the Permissions Management page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Permissions Management</h1>
          <p className="text-muted-foreground mt-1">Control sidebar visibility and access permissions per role</p>
        </div>
        <Badge className="bg-purple-100 text-purple-800 border-purple-200 px-4 py-2">
          <Shield className="h-4 w-4 mr-2" /> Super Admin Access
        </Badge>
      </div>

      {/* Info Alert */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Real-Time Updates</p>
            <p className="text-sm text-amber-700">Changes you make here will apply immediately when users refresh or login.</p>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="sidebar" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="sidebar" className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4" /> Sidebar Items
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" /> Roles
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> User Overrides
          </TabsTrigger>
        </TabsList>

        {/* Sidebar Items Tab */}
        <TabsContent value="sidebar" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Configure which sidebar items are visible to each role and their permissions.
            </p>
            <Button onClick={saveSidebarConfig} disabled={saving} data-testid="save-sidebar-config">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sidebarConfig.map(item => (
              <SidebarItemCard 
                key={item.item_key}
                item={item}
                roles={roles}
                onUpdate={handleSidebarItemUpdate}
              />
            ))}
          </div>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            View and manage role definitions. Super Admin role cannot be modified.
          </p>

          <div className="grid grid-cols-1 gap-4">
            {roles.map(role => (
              <RoleCard 
                key={role.role}
                role={role}
                onEdit={(r) => toast.info('Role editing coming soon')}
              />
            ))}
          </div>
        </TabsContent>

        {/* User Overrides Tab */}
        <TabsContent value="users" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Override role-based permissions for specific users. These overrides take priority over role settings.
          </p>

          <div className="grid grid-cols-1 gap-4">
            {users.map(u => (
              <UserOverrideCard 
                key={u.id}
                user={u}
                override={userOverrides[u.id]}
                onEdit={openUserOverrideDialog}
                onRemove={removeUserOverride}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* User Override Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Override Permissions for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label className="font-medium">Enable Permission Override</Label>
                <p className="text-xs text-muted-foreground">When enabled, user will have custom sidebar access instead of role-based</p>
              </div>
              <Switch 
                checked={selectedOverride.override_role}
                onCheckedChange={(v) => setSelectedOverride(prev => ({ ...prev, override_role: v }))}
              />
            </div>

            {selectedOverride.override_role && (
              <div className="space-y-4">
                <Label className="font-medium">Sidebar Items Access</Label>
                <div className="grid grid-cols-2 gap-2">
                  {sidebarConfig.map(item => {
                    const Icon = ICON_MAP[item.item_key] || Settings;
                    const isSelected = selectedOverride.sidebar_items.includes(item.item_key);
                    return (
                      <div 
                        key={item.item_key}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => toggleOverrideSidebarItem(item.item_key)}
                      >
                        <Icon className={`h-4 w-4 ${isSelected ? 'text-[#D4E100]' : ''}`} />
                        <span className="text-sm font-medium">{item.name || item.item_key}</span>
                        {isSelected && <Check className="h-4 w-4 ml-auto text-[#D4E100]" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>Cancel</Button>
            <Button onClick={saveUserOverride} disabled={saving} data-testid="save-override-btn">
              {saving ? 'Saving...' : 'Save Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionsPage;
